import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Edit,
  GripVertical,
  Trash2,
  Loader2,
  Save,
  X,
  ListOrdered,
} from "lucide-react";
import {
  useWorkItemTemplates,
  useCreateWorkItemTemplates,
  useUpdateWorkItemTemplate,
  useDeleteWorkItemTemplate,
} from "@/hooks/useWorkItemTemplates";
import { WorkItemTemplate } from "@/services/workItemTemplateApi";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface DepartmentProcess {
  id: string;
  name: string;
  description: string;
  departmentId: string;
  executionOrder: number;
}

interface DepartmentProcessesProps {
  departmentId: string;
  departmentName: string;
}

interface SortableProcessItemProps {
  process: DepartmentProcess;
  index: number;
  onEdit: (process: DepartmentProcess) => void;
  onDelete: (processId: string) => void;
  isReorderMode: boolean;
}

function SortableProcessItem({
  process,
  index,
  onEdit,
  onDelete,
  isReorderMode,
}: SortableProcessItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: process.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`transition-all ${
        isDragging ? "shadow-lg scale-105" : "hover:shadow-md"
      }`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          {isReorderMode && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600"
            >
              <GripVertical className="h-5 w-5" />
            </div>
          )}
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            步驟{index + 1}
          </div>
          <div className="flex-1">
            <h5 className="font-semibold text-base">{process.name}</h5>
            <p className="text-sm text-muted-foreground mt-1">
              {process.description}
            </p>
          </div>
          {!isReorderMode && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => onEdit(process)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-red-100 dark:hover:bg-red-900"
                onClick={() => onDelete(process.id)}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function DepartmentProcesses({
  departmentId,
}: DepartmentProcessesProps) {
  const { data: workItems, isLoading } = useWorkItemTemplates(departmentId);
  const createWorkItemsMutation = useCreateWorkItemTemplates(departmentId);
  const updateWorkItemMutation = useUpdateWorkItemTemplate(departmentId);
  const deleteWorkItemMutation = useDeleteWorkItemTemplate(departmentId);

  const [processes, setProcesses] = useState<DepartmentProcess[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newProcessName, setNewProcessName] = useState("");
  const [newProcessDescription, setNewProcessDescription] = useState("");

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProcess, setEditingProcess] =
    useState<DepartmentProcess | null>(null);
  const [editProcessName, setEditProcessName] = useState("");
  const [editProcessDescription, setEditProcessDescription] = useState("");

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingProcessId, setDeletingProcessId] = useState<string | null>(
    null
  );

  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedProcesses, setReorderedProcesses] = useState<
    DepartmentProcess[]
  >([]);

  useEffect(() => {
    if (workItems) {
      const mappedProcesses = workItems.map((item: WorkItemTemplate) => ({
        id: item.id,
        name: item.name,
        description: item.description || "",
        departmentId: item.departmentId,
        executionOrder: item.executionOrder,
      }));
      const sorted = mappedProcesses.sort(
        (a, b) => a.executionOrder - b.executionOrder
      );
      setProcesses(sorted);
      setReorderedProcesses(sorted);
    }
  }, [workItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setReorderedProcesses((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({
          ...item,
          executionOrder: index + 1,
        }));
      });
    }
  };

  const handleStartReorder = () => {
    setIsReorderMode(true);
    setReorderedProcesses([...processes]);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedProcesses([...processes]);
  };

  const handleSaveReorder = async () => {
    // Find items that have changed order
    const updates = reorderedProcesses.filter((item, index) => {
      const original = processes.find((p) => p.id === item.id);
      return original && original.executionOrder !== item.executionOrder;
    });

    // Update all changed items
    try {
      await Promise.all(
        updates.map((item) =>
          updateWorkItemMutation.mutateAsync({
            workItemId: item.id,
            data: { executionOrder: item.executionOrder },
          })
        )
      );
      setIsReorderMode(false);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleAddProcess = async () => {
    if (newProcessName.trim()) {
      await createWorkItemsMutation.mutateAsync([
        {
          name: newProcessName,
          description: newProcessDescription || undefined,
          executionOrder: processes.length + 1,
          isActive: true,
        },
      ]);
      setNewProcessName("");
      setNewProcessDescription("");
      setIsDialogOpen(false);
    }
  };

  const handleEditClick = (process: DepartmentProcess) => {
    setEditingProcess(process);
    setEditProcessName(process.name);
    setEditProcessDescription(process.description);
    setIsEditDialogOpen(true);
  };

  const handleUpdateProcess = async () => {
    if (!editProcessName.trim() || !editingProcess) return;

    await updateWorkItemMutation.mutateAsync({
      workItemId: editingProcess.id,
      data: {
        name: editProcessName,
        description: editProcessDescription || undefined,
      },
    });

    setIsEditDialogOpen(false);
    setEditingProcess(null);
  };

  const handleDeleteClick = (processId: string) => {
    setDeletingProcessId(processId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProcessId) return;

    await deleteWorkItemMutation.mutateAsync(deletingProcessId);
    setIsDeleteDialogOpen(false);
    setDeletingProcessId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-lg font-semibold">工序管理</h4>
          <p className="text-sm text-muted-foreground">
            {isReorderMode ? "拖曳調整工序順序" : "管理部門的工序項目"}
          </p>
        </div>
        <div className="flex gap-2">
          {!isReorderMode ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={handleStartReorder}
                disabled={processes.length < 2}
              >
                <ListOrdered className="h-4 w-4" />
                調整順序
              </Button>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    新增工序
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>新增工序</DialogTitle>
                    <DialogDescription>為此部門新增工序項目</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="processName">工序名稱</Label>
                      <Input
                        id="processName"
                        placeholder="請輸入工序名稱"
                        value={newProcessName}
                        onChange={(e) => setNewProcessName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="processDescription">工序描述</Label>
                      <Textarea
                        id="processDescription"
                        placeholder="請描述此工序的內容"
                        value={newProcessDescription}
                        onChange={(e) =>
                          setNewProcessDescription(e.target.value)
                        }
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                      >
                        取消
                      </Button>
                      <Button
                        onClick={handleAddProcess}
                        disabled={createWorkItemsMutation.isPending}
                      >
                        {createWorkItemsMutation.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        新增
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={handleCancelReorder}
              >
                <X className="h-4 w-4" />
                取消
              </Button>
              <Button
                size="sm"
                className="flex items-center gap-2"
                onClick={handleSaveReorder}
                disabled={updateWorkItemMutation.isPending}
              >
                {updateWorkItemMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                儲存順序
              </Button>
            </>
          )}
        </div>
      </div>

      {isReorderMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={reorderedProcesses.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid gap-3">
              {reorderedProcesses.map((process, index) => (
                <SortableProcessItem
                  key={process.id}
                  process={process}
                  index={index}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  isReorderMode={isReorderMode}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid gap-3">
          {processes.map((process, index) => (
            <SortableProcessItem
              key={process.id}
              process={process}
              index={index}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              isReorderMode={isReorderMode}
            />
          ))}
        </div>
      )}

      {processes.length === 0 && (
        <div className="text-center py-12 px-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
              尚未設定任何工序
            </p>
            <p className="text-sm text-muted-foreground">
              點擊上方按鈕新增第一個工序
            </p>
          </div>
        </div>
      )}

      {/* Edit Process Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>編輯工序</DialogTitle>
            <DialogDescription>修改工序的名稱和描述</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editProcessName">工序名稱</Label>
              <Input
                id="editProcessName"
                value={editProcessName}
                onChange={(e) => setEditProcessName(e.target.value)}
                placeholder="請輸入工序名稱"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editProcessDescription">工序描述</Label>
              <Textarea
                id="editProcessDescription"
                value={editProcessDescription}
                onChange={(e) => setEditProcessDescription(e.target.value)}
                placeholder="請描述此工序的內容"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                取消
              </Button>
              <Button
                onClick={handleUpdateProcess}
                disabled={
                  !editProcessName.trim() || updateWorkItemMutation.isPending
                }
              >
                {updateWorkItemMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                更新
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Process Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>確認刪除</DialogTitle>
            <DialogDescription>
              您確定要刪除這個工序嗎？此操作無法復原。
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteWorkItemMutation.isPending}
            >
              {deleteWorkItemMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              確認刪除
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
